import { getId } from 'assets/utils'
import { loginApi, addressApi } from './axiosClient'

export default {
  loadAnalytics: async (userIds, fromDate, toDate) => {
    const userId = getId()

    const analytics = await addressApi.get(`drive-logs/${userId.sub}/analyst/`, {
      params: {
        userIds: userIds.join(','),
        fromDate,
        toDate,
      },
    })
    return analytics
  },
}

/**
 * Fetch total time spent by a user or group of users.
 * @param {string} userId - Logged-in user ID (required).
 * @param {Array<string>} [userIds] - Optional list of user IDs to filter.
 * @param {string} [fromDate] - Optional from date (yyyy-mm-dd).
 * @param {string} [toDate] - Optional to date (yyyy-mm-dd).
 * @param {boolean} [includeDates=false] - Whether to include date parameters.
 * @returns {Promise<number>} Total time in seconds.
 */
export async function fetchTotalTimeSpent(
  userId,
  userIds = [],
  fromDate,
  toDate,
  includeDates = false,
) {
  try {
    const params = {}

    if (userIds.length > 0) {
      params.userIds = userIds.join(',')
    }

    // Only include dates if explicitly requested
    if (includeDates && fromDate) {
      params.fromDate = fromDate
    }
    if (includeDates && toDate) {
      params.toDate = toDate
    }

    const response = await loginApi.get(`users/${userId}/totalTime`, { params })

    if (response && response.data && typeof response.data.totalTimeSpend === 'number') {
      return response.data.totalTimeSpend
    }

    return 0 // Default to 0 if response is invalid.
  } catch (error) {
    console.error('Error fetching total time spent:', error)
    return 0 // Default to 0 in case of an error.
  }
}

/**
 * Fetch average scores per tour by a user or group of users.
 * @param {string} userId - Logged-in user ID (required).
 * @param {Array<string>} [userIds] - Optional list of user IDs to filter.
 * @param {string} [fromDate] - Optional from date (yyyy-mm-dd).
 * @param {string} [toDate] - Optional to date (yyyy-mm-dd).
 * @param {boolean} [includeDates=false] - Whether to include date parameters.
 * @returns {Promise<Array>} Array of tour score data.
 */
export async function fetchAverageScoresPerTour(
  userId,
  userIds = [],
  fromDate,
  toDate,
  includeDates = false,
) {
  try {
    const params = {
      page: 1,
      size: 100,
    }

    if (userIds.length > 0) {
      params.userIds = userIds.join(',')
    }

    // Only include dates if explicitly requested
    if (includeDates && fromDate) {
      params.fromDate = fromDate
    }
    if (includeDates && toDate) {
      params.toDate = toDate
    }

    const response = await loginApi.get(`users/${userId}/average-scores-per-tour`, { params })

    if (response && response.data && Array.isArray(response.data)) {
      return response.data
    }

    return [] // Default to empty array if response is invalid.
  } catch (error) {
    console.error('Error fetching average scores per tour:', error)
    return [] // Default to empty array in case of an error.
  }
}

/**
 * Fetch average scores per level for a specific tour by a user or group of users.
 * @param {string} userId - Logged-in user ID (required).
 * @param {string} tourId - Tour ID (required).
 * @param {Array<string>} [userIds] - Optional list of user IDs to filter.
 * @param {string} [fromDate] - Optional from date (yyyy-mm-dd).
 * @param {string} [toDate] - Optional to date (yyyy-mm-dd).
 * @param {boolean} [includeDates=false] - Whether to include date parameters.
 * @returns {Promise<Array>} Array of level score data.
 */
export async function fetchAverageScoresPerLevel(
  userId,
  tourId,
  userIds = [],
  fromDate,
  toDate,
  includeDates = false,
) {
  try {
    const params = {
      tourId,
    }

    if (userIds.length > 0) {
      params.userIds = userIds.join(',')
    }

    // Only include dates if explicitly requested
    if (includeDates && fromDate) {
      params.fromDate = fromDate
    }
    if (includeDates && toDate) {
      params.toDate = toDate
    }

    const response = await loginApi.get(`users/${userId}/average-scores-per-level`, { params })

    if (response && response.data && Array.isArray(response.data)) {
      return response.data
    }

    return [] // Default to empty array if response is invalid.
  } catch (error) {
    console.error('Error fetching average scores per level:', error)
    return [] // Default to empty array in case of an error.
  }
}
